// Constants
import { KeyCode } from "@ms/uno-constants/lib/local/KeyCode";
import { ElementIds } from "@ms/uno-constants/lib/local/UIConstants";
// Controls
import { DynamicIcon } from "@ms/planner-generic-controls/lib/local/dynamicIcon/DynamicIcon";
import { IconButton } from "@ms/planner-generic-controls/lib/local/iconButton/IconButton";
import { SimpleBreadcrumb } from "@ms/planner-generic-controls/lib/local/simpleBreadcrumb/SimpleBreadcrumb";
import { PlanIcon } from "@ms/planner-presentational-controls/lib/local/planIcon/PlanIcon";
// Icons
import { PinFilled } from "@ms/planner-generic-controls/lib/local/icons/index";
// Styles
import { useLeftNavOptionStyles } from "./LeftNavOption.styles";
// Utilities
import { mergeClasses, Tooltip } from "@fluentui/react-components";
import { KeyboardUtilities } from "@ms/uno-utilities/lib/local/KeyboardUtilities";
import * as React from "react";
import { Strings } from "../../../utilities/CultureUtilities";
/**
 * Checks whether the text inside a container is truncated (overflowing).
 * This function is used to determine tooltip visibility based on text overflow.
 */ export function isTextTruncated(container) {
    const span = container?.querySelector("span");
    return !!span && span.scrollWidth > span.clientWidth;
}
/** Stable, non-PII data-testid per well-known left-nav destination. */ const LEFT_NAV_OPTION_TEST_IDS = {
    [ElementIds.MyDayNavButton]: "left-nav-option-myday",
    [ElementIds.MyTasksNavButton]: "left-nav-option-mytasks",
    [ElementIds.MyPlansNavButton]: "left-nav-option-myplans",
    [ElementIds.MyPortfoliosNavButton]: "left-nav-option-myportfolios"
};
/**
 * Control for each option in the LeftNav
 */ export const LeftNavOption = ({ ariaPosInSet, ariaSetSize, name, url, id, selected, className, icon, containerType, containerPhoto, breadcrumbItems, onTogglePin, onSelect, keyCodeMapper })=>{
    const styles = useLeftNavOptionStyles();
    const [displayFocusedIcon, setDisplayFocusedIcon] = React.useState(false);
    const [planNameTooltipVisible, setPlanNameTooltipVisible] = React.useState(false);
    const textRef = React.useRef(null);
    const handleListItemClick = (event)=>{
        if (onSelect) {
            event.preventDefault();
            event.stopPropagation();
            onSelect();
        }
    };
    const handleListItemKeyDown = (e)=>{
        const keyDowned = KeyboardUtilities.getKey(e);
        if (keyDowned === KeyCode.SPACE) {
            e.preventDefault();
        }
        if (KeyboardUtilities.keyCodeIsSelectionKey(keyDowned, keyCodeMapper)) {
            if (onSelect != null) {
                onSelect();
            }
        }
    };
    const renderOptionIcon = ()=>{
        const iconClasses = mergeClasses(styles.staticIcon, selected && styles.staticIconSelected, displayFocusedIcon && !selected && styles.staticIconHover);
        if (icon != null) {
            // Determine which icon to show based on focused state
            const showFocusedIcon = selected || displayFocusedIcon;
            const iconToRender = showFocusedIcon && icon.focusedIcon ? icon.focusedIcon : icon.normalIcon;
            return /*#__PURE__*/ React.createElement("div", {
                className: iconClasses
            }, /*#__PURE__*/ React.createElement(DynamicIcon, {
                name: name,
                focused: showFocusedIcon,
                icon: iconToRender,
                avatarProps: {
                    size: 24
                }
            }));
        }
        return /*#__PURE__*/ React.createElement("div", {
            className: mergeClasses(styles.dynamicIcon, styles.dynamicIconTextWrapper)
        }, /*#__PURE__*/ React.createElement(PlanIcon, {
            name: name,
            containerType: containerType,
            containerPhoto: containerPhoto,
            size: 24
        }));
    };
    const renderUnpinButton = ()=>{
        if (onTogglePin === undefined) {
            return null;
        }
        const onPinButtonClick = (event)=>{
            event.stopPropagation();
            event.preventDefault();
            onTogglePin();
        };
        return /*#__PURE__*/ React.createElement(IconButton, {
            className: styles.pinButton,
            icon: /*#__PURE__*/ React.createElement(PinFilled, null),
            onClick: onPinButtonClick,
            onMouseEnter: ()=>setPlanNameTooltipVisible(false),
            onMouseLeave: ()=>{
                if (isTextTruncated(textRef.current)) {
                    setPlanNameTooltipVisible(true);
                }
            },
            onFocus: ()=>setPlanNameTooltipVisible(false),
            title: Strings.LeftNav_UnpinText,
            "aria-label": Strings.LeftNav_UnpinText,
            "data-is-focusable": true,
            "data-pin-button": true,
            "data-testid": "pin-unpin-button"
        });
    };
    const combinedClassName = mergeClasses(className, styles.root, selected && styles.selected, displayFocusedIcon && !selected && styles.hoveredNotSelected);
    const linkContentsClassName = mergeClasses(styles.linkContents, selected && styles.linkContentsSelected);
    const leftNavItemClasses = mergeClasses(styles.leftNavItem, selected && styles.leftNavItemSelected, displayFocusedIcon && !selected && styles.leftNavItemHover);
    //data-tid is used for testing purpose by PAS tool
    const data_tid = `leftNavLink${name?.replace(/\s+/g, "") || ""}`;
    const dataTestId = LEFT_NAV_OPTION_TEST_IDS[id] ?? "left-nav-option";
    return /*#__PURE__*/ React.createElement(Tooltip, {
        content: name,
        relationship: "inaccessible",
        withArrow: true,
        visible: planNameTooltipVisible
    }, /*#__PURE__*/ React.createElement("li", {
        className: combinedClassName,
        "aria-label": name,
        "aria-posinset": ariaPosInSet,
        "aria-setsize": ariaSetSize,
        "data-is-focusable": true,
        "data-tid": data_tid,
        "data-testid": dataTestId,
        "aria-selected": selected,
        id: id,
        tabIndex: 0,
        onClick: handleListItemClick,
        onKeyDown: handleListItemKeyDown,
        onFocus: (e)=>{
            setDisplayFocusedIcon(true);
            // Only show tooltip on direct focus (not bubbled from child elements like the pin button)
            if (e.target === e.currentTarget && isTextTruncated(textRef.current)) {
                setPlanNameTooltipVisible(true);
            }
        },
        onBlur: ()=>{
            setDisplayFocusedIcon(false);
            setPlanNameTooltipVisible(false);
        },
        onMouseEnter: ()=>{
            setDisplayFocusedIcon(true);
            if (isTextTruncated(textRef.current)) {
                setPlanNameTooltipVisible(true);
            }
        },
        onMouseLeave: ()=>{
            setDisplayFocusedIcon(false);
            setPlanNameTooltipVisible(false);
        },
        role: "option"
    }, /*#__PURE__*/ React.createElement("a", {
        "data-testid": "left-nav-option-link",
        href: url,
        "data-is-focusable": "false"
    }, /*#__PURE__*/ React.createElement("div", {
        className: linkContentsClassName
    }, renderOptionIcon(), /*#__PURE__*/ React.createElement("div", {
        ref: textRef,
        className: styles.text
    }, /*#__PURE__*/ React.createElement("span", {
        className: leftNavItemClasses
    }, name), breadcrumbItems && breadcrumbItems.length > 0 && /*#__PURE__*/ React.createElement(SimpleBreadcrumb, {
        keySuffix: id,
        breadcrumbStrings: breadcrumbItems,
        customStyles: {
            root: styles.simpleBreadcrumb,
            item: selected ? styles.breadcrumbItemSelected : displayFocusedIcon ? styles.breadcrumbItemHover : styles.breadcrumbItem,
            chevron: selected ? styles.chevronSelected : displayFocusedIcon ? styles.chevronHover : styles.chevron
        }
    })), renderUnpinButton()))));
};
